import { useQuery } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"

export type ComplianceOverviewMetricKey =
  | "vatNumber"
  | "utrNumber"
  | "paymentInfo"
  | "licencesChecked"
  | "rightToWork"
  | "licenceFrontBack"
  | "proofOfAddress"
  | "bankStatement"
  | "serviceLevelAgreement"

export interface ComplianceOverviewMetric {
  key: ComplianceOverviewMetricKey
  title: string
  description: string
  total: number
  compliant: number
  percentage: number
}

export interface ComplianceOverviewResponse {
  total: number
  metrics: ComplianceOverviewMetric[]
}

export interface ComplianceOverviewFilters {
  searchQuery?: string
  locationFilter?: string
  sheriffTypeFilter?: string
  driverManagerTeamFilter?: string
  dspFilter?: string
  eligibilityFilter?: "all" | "Eligible" | "Ineligible" | "Failed" | "Pending"
}

export function useComplianceOverview(filters: ComplianceOverviewFilters = {}) {
  const {
    searchQuery = "",
    locationFilter = "",
    sheriffTypeFilter = "",
    driverManagerTeamFilter = "",
    dspFilter = "",
    eligibilityFilter = "all",
  } = filters

  return useQuery<ComplianceOverviewResponse>({
    queryKey: [
      "compliance-overview",
      searchQuery,
      locationFilter,
      sheriffTypeFilter,
      driverManagerTeamFilter,
      dspFilter,
      eligibilityFilter,
    ],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("compliance-overview", {
        body: {
          searchQuery,
          locationFilter,
          sheriffTypeFilter,
          driverManagerTeamFilter,
          dspFilter,
          eligibilityFilter,
        },
      })

      if (error) {
        throw new Error(error.message)
      }

      if (!data?.success) {
        throw new Error(data?.error || "Failed to load compliance overview")
      }

      return {
        total: data.total as number,
        metrics: data.metrics as ComplianceOverviewMetric[],
      }
    },
  })
}
